import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Person,
  School,
  Email,
  Phone,
  LocationOn,
  Star,
  StarBorder,
  VideoLibrary,
  FamilyRestroom,
} from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { TRACK_EVENTS, isHttpUrl } from '@tnf-recruit/shared'
import { useStudentDetail } from '../hooks/useStudentSearch'
import { useRecruiterBookmarks } from '../hooks/useBookmarks'

/** Look up display name for an event ID */
const getEventName = (eventId: string) => TRACK_EVENTS.find(e => e.id === eventId)?.name ?? eventId

export const StudentDetailPage = () => {
  const { id } = useParams()
  const { data: student, isLoading, error } = useStudentDetail(id)
  const { bookmarks, addBookmark, removeBookmark } = useRecruiterBookmarks()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !student) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error ? 'Failed to load profile.' : 'Profile not found.'}
      </Alert>
    )
  }

  const pi = student.personalInfo ?? {}
  const ai = student.academicInfo ?? {}
  const ath = student.athleticInfo ?? {}
  const records = student.personalRecords ?? []
  const primaryEvents: string[] = ath.primaryEvents ?? []

  // Check if this student is bookmarked
  const existingBookmark = bookmarks.data?.find(b => b.studentId === student.id)
  const isBookmarked = !!existingBookmark

  const handleToggleBookmark = () => {
    if (isBookmarked && existingBookmark) {
      removeBookmark.mutate(existingBookmark.id)
    } else {
      addBookmark.mutate({ studentId: student.id })
    }
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                    <Person fontSize="large" />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" gutterBottom>
                    {pi.firstName} {pi.lastName}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {ai.highSchool}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip
                      label={`Class of ${(student as any).graduationYear ?? ai.graduationYear}`}
                      color="primary"
                    />
                    <Chip label={`GPA: ${(student as any).gpa ?? ai.gpa}`} />
                    {ai.satScore && <Chip label={`SAT: ${ai.satScore}`} />}
                    {ai.actScore && <Chip label={`ACT: ${ai.actScore}`} />}
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      startIcon={<Email />}
                      component="a"
                      href={pi.email ? `mailto:${pi.email}` : undefined}
                      disabled={!pi.email}
                    >
                      Contact
                    </Button>
                    <Button
                      variant={isBookmarked ? 'contained' : 'outlined'}
                      color={isBookmarked ? 'warning' : 'primary'}
                      startIcon={isBookmarked ? <Star /> : <StarBorder />}
                      onClick={handleToggleBookmark}
                      disabled={addBookmark.isPending || removeBookmark.isPending}
                    >
                      {isBookmarked ? 'Saved' : 'Save'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={
                      pi.email ? <a href={`mailto:${pi.email}`}>{pi.email}</a> : 'Not provided'
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mobile phone"
                    secondary={
                      pi.mobilePhone ? (
                        <a href={`tel:${pi.mobilePhone}`}>{pi.mobilePhone}</a>
                      ) : (
                        'Not provided'
                      )
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary="Location"
                    secondary={`${pi.address?.city ?? ''}, ${pi.address?.state ?? ''}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <School />
                  </ListItemIcon>
                  <ListItemText primary="School" secondary={ai.highSchool} />
                </ListItem>
                {pi.bio && (
                  <ListItem>
                    <ListItemIcon>
                      <Person />
                    </ListItemIcon>
                    <ListItemText primary="Bio" secondary={pi.bio} />
                  </ListItem>
                )}
                {pi.parentGuardian?.email && (
                  <ListItem>
                    <ListItemIcon>
                      <FamilyRestroom />
                    </ListItemIcon>
                    <ListItemText
                      primary="Parent/Guardian"
                      secondary={
                        <>
                          {pi.parentGuardian.firstName} {pi.parentGuardian.lastName}
                          <br />
                          <a href={`mailto:${pi.parentGuardian.email}`}>
                            {pi.parentGuardian.email}
                          </a>
                          {pi.parentGuardian.phoneNumber && (
                            <>
                              {' · '}
                              <a href={`tel:${pi.parentGuardian.phoneNumber}`}>
                                {pi.parentGuardian.phoneNumber}
                              </a>
                            </>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Athletic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Athletic Information
              </Typography>
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Events
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {primaryEvents.map((eventId: string) => (
                    <Chip key={eventId} label={getEventName(eventId)} color="primary" />
                  ))}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Personal Records
              </Typography>
              {records.length > 0 ? (
                records.map((record: any) => (
                  <Box
                    key={record.id ?? record.eventId}
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography variant="body2">{getEventName(record.eventId)}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {record.mark}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No personal records yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Achievements */}
        {ath.achievements && ath.achievements.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Achievements
                </Typography>
                {ath.achievements.map((a: any) => (
                  <Box key={a.id} mb={2}>
                    <Typography variant="subtitle2" color="primary">
                      {a.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {a.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Coach References */}
        {ath.coachReferences && ath.coachReferences.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Coach References
                </Typography>
                {ath.coachReferences.map((coach: any) => (
                  <Box key={coach.id} mb={2}>
                    <Typography variant="subtitle2">{coach.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {coach.title} - {coach.school}
                    </Typography>
                    <Typography variant="body2">
                      <Email sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      {coach.email}
                    </Typography>
                    <Typography variant="body2">
                      <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      {coach.phone}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Video Highlights */}
        {((ath.videoLinks && ath.videoLinks.length > 0) || ath.recruitingVideoLink) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Video Highlights
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {ath.recruitingVideoLink && isHttpUrl(ath.recruitingVideoLink) && (
                    <Button
                      variant="outlined"
                      startIcon={<VideoLibrary />}
                      href={ath.recruitingVideoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Watch Recruiting Video
                    </Button>
                  )}
                  {ath.videoLinks
                    ?.filter((link: string) => isHttpUrl(link))
                    .map((link: string, index: number) => (
                      <Button
                        key={index}
                        variant="outlined"
                        startIcon={<VideoLibrary />}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch Video {index + 1}
                      </Button>
                    ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
